import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import GranjaMovimientosLinkButton from '../../components/granja/GranjaMovimientosLinkButton';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import ajusteInventarioService from '../../services/ajusteInventarioService';
import granjaService, { GranjaConfig, resolveNroSucOperativo } from '../../services/granjaService';
import productoDepositoService from '../../services/productoDepositoService';
import ubicacionService, { Ubicacion } from '../../services/ubicacionService';
import { getApiErrorMessage } from '../../utils/apiError';
import { toLocalDateStringYMD } from '../../utils/formatters';
import {
  formatMaplesYUnidades,
  HUEVOS_POR_MAPLE,
  maplesYHuevosAHuevos,
} from '../../utils/granjaCantidades';

interface LineaDespacho {
  codigoArticulo: string;
  label: string;
  saldoGranja: number;
  maples: string;
  huevos: string;
}

const soloEnteros = (value: string) => value.replace(/\D/g, '');

const GranjaCargaChata: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferidaNro } = useSucursalesUsuarioContext();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [destino, setDestino] = useState<number | ''>('');
  const [lineas, setLineas] = useState<LineaDespacho[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, ubics] = await Promise.all([
          granjaService.getConfig(),
          ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 200 }),
        ]);
        setConfig(cfg);
        setUbicaciones(ubics);

        const arts = [
          ...(cfg.articulosClasificacion ?? []).map((a) => ({
            codigo: a.codigoArticulo,
            label: `${a.calibre} ${a.color}`,
          })),
          ...(cfg.articuloCascado
            ? [{ codigo: cfg.articuloCascado, label: 'Cascado' }]
            : []),
        ];

        const withSaldo: LineaDespacho[] = [];
        for (const a of arts) {
          let saldo = 0;
          if (cfg.depositoGranja) {
            try {
              const res = await productoDepositoService.getDepositos(a.codigo);
              const dep = res.depositos.find((d) => d.nroUbicacion === cfg.depositoGranja);
              saldo = Math.round(Number(dep?.cantidad ?? 0));
            } catch {
              saldo = 0;
            }
          }
          withSaldo.push({
            codigoArticulo: a.codigo,
            label: a.label,
            saldoGranja: saldo,
            maples: '',
            huevos: '',
          });
        }
        setLineas(withSaldo);
      } catch {
        enqueueSnackbar('Error al cargar datos', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  const destinosOpts = ubicaciones
    .filter((u) => !config?.depositoGranja || u.nroUbicacion !== config.depositoGranja)
    .map((u) => ({
      value: u.nroUbicacion,
      label: `${u.nroUbicacion} — ${u.nombreUbicacion}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));

  const destinoLabel =
    destinosOpts.find((o) => o.value === destino)?.label ??
    (destino ? String(destino) : '');

  const itemsDespachar = lineas
    .map((l) => {
      const cant = maplesYHuevosAHuevos(Number(l.maples) || 0, Number(l.huevos) || 0);
      return { ...l, cant };
    })
    .filter((l) => l.cant > 0);

  const erroresStock = itemsDespachar.filter((l) => l.cant > l.saldoGranja);

  const updateLinea = (codigo: string, field: 'maples' | 'huevos', value: string) => {
    const cleaned = soloEnteros(value);
    setLineas((prev) =>
      prev.map((x) => (x.codigoArticulo === codigo ? { ...x, [field]: cleaned } : x)),
    );
  };

  const handleAbrirConfirmacion = () => {
    if (!config?.depositoGranja || !destino) {
      enqueueSnackbar('Seleccioná el depósito / sitio destino', { variant: 'warning' });
      return;
    }
    if (itemsDespachar.length === 0) {
      enqueueSnackbar('Ingresá al menos una cantidad', { variant: 'warning' });
      return;
    }
    if (erroresStock.length > 0) {
      enqueueSnackbar('Hay cantidades mayores al saldo de Granja', { variant: 'error' });
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmar = async () => {
    if (!config?.depositoGranja || !destino) return;
    try {
      setSaving(true);
      const nroSuc = await resolveNroSucOperativo(preferidaNro);
      if (!nroSuc) {
        enqueueSnackbar('No hay sucursal operativa asignada al usuario', { variant: 'warning' });
        return;
      }
      await ajusteInventarioService.createAjuste(
        {
          fechaEmision: diaOperativo,
          nroSuc,
          descripcion: `Despacho prod. ${destino}`,
          nroUbicacionOrigen: config.depositoGranja,
          nroUbicacionDestino: destino,
          motivoCodigo: 'DESPACHO_PROD',
        } as never,
        itemsDespachar.map((l) => ({
          codigoArticulo: l.codigoArticulo,
          descripcion: l.label,
          cantidad: l.cant,
          costo: 0,
          notas: `Despacho producción — ${formatMaplesYUnidades(l.cant / HUEVOS_POR_MAPLE)} (${l.cant} u.)`,
        })) as never,
        'transferencia',
      );
      enqueueSnackbar('Despacho registrado', { variant: 'success' });
      setConfirmOpen(false);
      setLineas((prev) =>
        prev.map((l) => {
          const mov = itemsDespachar.find((i) => i.codigoArticulo === l.codigoArticulo);
          if (!mov) return { ...l, maples: '', huevos: '' };
          return {
            ...l,
            saldoGranja: Math.max(0, l.saldoGranja - mov.cant),
            maples: '',
            huevos: '',
          };
        }),
      );
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al despachar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Despacho de producción
              </Typography>
              <GranjaMovimientosLinkButton tipo="DESPACHO_PROD" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Transferencia Granja → depósito / sitio. Cantidades en maples + huevos. No permite saldos
              negativos.
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="destino-label">Depósito / sitio destino</InputLabel>
              <Select
                labelId="destino-label"
                label="Depósito / sitio destino"
                value={destino}
                onChange={(e) => setDestino(e.target.value as number)}
              >
                {destinosOpts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {destinosOpts.length === 0 ? (
              <Alert severity="info">No hay depósitos / sitios disponibles para seleccionar.</Alert>
            ) : null}

            {lineas.map((l) => {
              const cant = maplesYHuevosAHuevos(Number(l.maples) || 0, Number(l.huevos) || 0);
              const supera = cant > 0 && cant > l.saldoGranja;
              const saldoMaples = l.saldoGranja / HUEVOS_POR_MAPLE;
              return (
                <Box key={l.codigoArticulo}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
                    {l.label} ({l.codigoArticulo})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Saldo: {l.saldoGranja.toLocaleString('es-AR')} huevos ({saldoMaples.toFixed(2)}{' '}
                    maples)
                  </Typography>
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label="Maples"
                      value={l.maples}
                      onChange={(e) => updateLinea(l.codigoArticulo, 'maples', e.target.value)}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      error={supera}
                      fullWidth
                    />
                    <TextField
                      label="Huevos"
                      value={l.huevos}
                      onChange={(e) => updateLinea(l.codigoArticulo, 'huevos', e.target.value)}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      error={supera}
                      helperText={supera ? 'Supera el saldo en Granja' : undefined}
                      fullWidth
                    />
                  </Stack>
                </Box>
              );
            })}

            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={saving || itemsDespachar.length === 0 || erroresStock.length > 0}
              onClick={handleAbrirConfirmacion}
              sx={{ minHeight: 56, fontWeight: 700 }}
            >
              Despachar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirmar despacho</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Destino
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {destinoLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Cantidades por calibre
            </Typography>
            {itemsDespachar.map((l) => (
              <Stack
                key={l.codigoArticulo}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="body1">
                  {l.label}{' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({l.codigoArticulo})
                  </Typography>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {formatMaplesYUnidades(l.cant / HUEVOS_POR_MAPLE)} ({l.cant} u.)
                </Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirmar} disabled={saving}>
            {saving ? 'Confirmando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GranjaCargaChata;
